import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, RotateCcw, Check, Smartphone } from 'lucide-react';

/**
 * Fullscreen modal for capturing client signature on mobile.
 * Returns { signature_base64, signer_name } via onConfirm.
 */
export const SignaturePadModal = ({ open, onClose, onConfirm, initialSignerName = '' }) => {
  const sigRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [signerName, setSignerName] = useState(initialSignerName || '');
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 240 });

  // Resize canvas when modal opens or window resizes
  useEffect(() => {
    if (!open) return;
    const updateSize = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      setCanvasSize({
        width: Math.max(280, Math.floor(rect.width)),
        height: Math.max(180, Math.floor(rect.height)),
      });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSize);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setSignerName(initialSignerName || '');
      setIsEmpty(true);
      setTimeout(() => sigRef.current?.clear(), 50);
    }
  }, [open, initialSignerName]);

  if (!open) return null;

  const handleClear = () => {
    sigRef.current?.clear();
    setIsEmpty(true);
  };

  const handleConfirm = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      alert('Per favore firma prima di confermare');
      return;
    }
    // Use raw canvas (avoid trim-canvas dependency runtime issue)
    const canvas = sigRef.current.getCanvas();
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.split(',')[1];
    onConfirm({ signature_base64: base64, signer_name: signerName.trim() });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900 flex flex-col"
      data-testid="signature-pad-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800 text-white">
        <button
          onClick={onClose}
          className="p-2 -ml-2 active:scale-95"
          data-testid="signature-cancel-btn"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2 text-sm">
          <Smartphone className="h-4 w-4 text-amber-400 rotate-90" />
          <span className="text-slate-200">Ruota in orizzontale per firmare meglio</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleClear}
            disabled={isEmpty}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm font-medium active:scale-95"
            data-testid="signature-clear-header-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Cancella
          </button>
          <button
            onClick={handleConfirm}
            disabled={isEmpty}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-sm font-medium active:scale-95"
            data-testid="signature-confirm-btn"
          >
            <Check className="h-4 w-4" />
            Conferma
          </button>
        </div>
      </div>

      {/* Signer name display (read-only — comes from selected reference person) */}
      <div className="px-4 py-3 bg-slate-800 border-b border-slate-700 flex items-center gap-3" data-testid="signature-signer-name-display">
        <span className="text-xs uppercase tracking-wide text-slate-400 flex-shrink-0">Firma di:</span>
        <span className="text-white text-sm font-medium truncate" data-testid="signature-signer-name">
          {signerName || '—'}
        </span>
      </div>

      {/* Canvas */}
      <div ref={wrapperRef} className="flex-1 bg-white relative" data-testid="signature-canvas-wrapper">
        <SignatureCanvas
          ref={sigRef}
          penColor="#0f172a"
          minWidth={1.5}
          maxWidth={3}
          throttle={8}
          canvasProps={{
            width: canvasSize.width,
            height: canvasSize.height,
            className: 'block w-full h-full touch-none',
            'data-testid': 'signature-canvas',
          }}
          onBegin={() => setIsEmpty(false)}
        />
        <div className="absolute bottom-3 left-0 right-0 text-center pointer-events-none">
          <span className="text-slate-300 text-xs">— Firma con il dito sopra —</span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-slate-800 border-t border-slate-700 flex items-center justify-between">
        <button
          onClick={handleClear}
          className="flex items-center gap-2 px-3 py-2 text-slate-200 hover:text-white active:scale-95"
          data-testid="signature-clear-btn"
        >
          <RotateCcw className="h-4 w-4" />
          Pulisci
        </button>
        <span className="text-xs text-slate-400">Tocca lo schermo per firmare</span>
      </div>
    </div>
  );
};

export default SignaturePadModal;
